import styles from './correctHoardPriceModal.module.css'
import { useEffect, useMemo, useRef, useState } from "react"
import { BooleanField, FieldType, FieldValue, Form, FormField, Modal, ModalId, O, StaticValue, useUIActions, useUIState } from "../../../../../features/ui"
import { useDataTypesHooks } from "../../../../../features/data-types"
import { Product, ProductSelectField, useProductLoaders } from "../../../../products"
import { DeliveryOrder } from "../../../../deliveries"
import { round } from "../../../../../utils/typeUtils"
import { HoardOrder } from "../../../state/types"
import { hoardHelpers, useHoardHooks } from "../../../hooks/hoardHooks"
import { getHoardPriceCorrectionAmount, planHoardPriceCorrection } from "../../../utils/hoardPriceCorrection"
import { useActions } from "./actions"

type Props = {
    hoard: HoardOrder
    deliveryOrders: DeliveryOrder[]
    onCorrected?: () => Promise<void>
}

const CorrectHoardPriceForm = (props: Props) => {
    const { hoard, deliveryOrders, onCorrected } = props

    const [products, setProducts] = useState<Product[]>([])
    const [productId, setProductId] = useState<O<string>>()
    const [newPrice, setNewPrice] = useState<O<number>>()
    const [focusProduct, setFocusProduct] = useState(false)
    const [emitNow, setEmitNow] = useState(true)

    const { formatPrice, formatNumber } = useDataTypesHooks()
    const { getPendingCredit } = useHoardHooks()

    const productLoaders = useProductLoaders()
    const { submitPriceCorrection } = useActions(onCorrected)
    const { closeModal } = useUIActions()

    useEffect(() => {
        productLoaders().loadProducts(Object.keys(hoard.pricesList)).then(setProducts)
    }, [hoard])

    // The product field gets the focus once the modal finished opening, to start typing right away
    useEffect(() => {
        const timeout = setTimeout(() => setFocusProduct(true), 500)
        return () => clearTimeout(timeout)
    }, [])

    const deliveredByItem = useMemo(() => hoardHelpers.getNetDeliveredByItem(deliveryOrders), [deliveryOrders])

    const plan = useMemo(() => productId && newPrice !== undefined ?
        planHoardPriceCorrection(hoard, productId, newPrice, deliveredByItem) :
        undefined
    , [hoard, productId, newPrice, deliveredByItem])

    const returnedBalance = useMemo(() => plan?.isDecrease ?
        round(getPendingCredit({ ...hoard, items: plan.items }) - getPendingCredit(hoard)) :
        0
    , [plan, hoard])

    const currentPrice = productId ? hoard.pricesList[productId] : undefined
    const productItems = hoard.items.filter(item => item.productId === productId)
    const deliveredQuantity = productItems.reduce((sum, item) => sum + (deliveredByItem.get(item.id) || 0), 0)
    const invoicedQuantity = productItems.reduce((sum, item) => sum + Math.min(item.invoiceStats?.invoiced || 0, deliveredByItem.get(item.id) || 0), 0)

    const creditNoteAmount = plan ? getHoardPriceCorrectionAmount(plan.creditNoteParts) : 0

    const describeResult = () => {
        if (!plan || plan.newPrice === plan.previousPrice) return '-'
        if (!plan.isDecrease) {
            return <ul className={styles.result}>
                <li><b>Lista de precios:</b> se corrige. El nuevo precio aplica solo a las próximas entregas.</li>
            </ul>
        }
        // Invoiced for its value: that invoice wasn't for products, so there is nothing to credit
        const invoicedInFull = !!hoard.invoiceStats?.fullInvoiceIssued && plan.invoicedQuantity > 0
        const describeCreditNote = () => {
            if (creditNoteAmount > 0) return `NC fiscal por ${formatPrice(creditNoteAmount)} (${formatNumber(plan.invoicedQuantity)} u. facturadas).`
            if (invoicedInFull) return 'no se genera. El acopio ya está facturado completo: la diferencia queda como saldo disponible para retirar.'
            return 'no se genera, porque no hay unidades facturadas.'
        }
        return <ul className={styles.result}>
            <li><b>Lista de precios:</b> se corrige, junto con el precio de lo ya entregado.</li>
            <li><b>Saldo del acopio:</b> {returnedBalance > 0 ? `vuelven ${formatPrice(returnedBalance)}.` : 'no cambia: no hay unidades entregadas.'}</li>
            <li><b>Nota de crédito:</b> {describeCreditNote()}</li>
        </ul>
    }

    const fields: FormField[] = [
        {
            name: 'productId',
            type: FieldType.TABLE,
            label: 'Producto',
            loading: products.length === 0,
            render: () => <ProductSelectField
                productList={products}
                branchId={hoard.branchId}
                defaultValue={productId}
                focus={focusProduct}
                onChange={value => setProductId(value)}
            />
        },
        ...(productId ? [
            {
                name: 'currentPrice',
                type: FieldType.PRICE,
                label: 'Precio Actual',
                hint: 'Precio de la lista del acopio, con IVA.',
                render: () => <StaticValue type={FieldType.PRICE} value={currentPrice} />
            },
            {
                name: 'quantities',
                type: FieldType.TEXT,
                label: 'Unidades',
                render: () => <>{formatNumber(deliveredQuantity)} entregadas · {formatNumber(invoicedQuantity)} facturadas</>
            },
            {
                name: 'newPrice',
                type: FieldType.PRICE,
                label: 'Precio Correcto',
                min: 0,
                onChange: (value?: FieldValue) => setNewPrice(value as O<number>),
                validate: () => newPrice === currentPrice ? 'El precio correcto tiene que ser distinto del actual' : null
            },
            {
                name: 'result',
                type: FieldType.TEXT,
                label: 'Resultado',
                render: () => <>{describeResult()}</>
            },
            ...(creditNoteAmount > 0 ? [{
                name: 'emitNow',
                type: FieldType.BOOLEAN,
                label: 'Emitir a ARCA',
                render: () => <BooleanField
                    id="emit-price-correction-note"
                    label={`Emitir la NC fiscal por ${formatPrice(creditNoteAmount)} a ARCA ahora`}
                    value={emitNow}
                    optional
                    onChange={value => setEmitNow(!!value)}
                    hint="Si no, queda pendiente para emitir desde Documentos."
                />
            }] : [])
        ] : [])
    ]

    // Repeated clicks or Enter before the spinner shows up would correct the price twice
    const submittingRef = useRef(false)
    const [submitting, setSubmitting] = useState(false)

    const onSubmit = async () => {
        if (submittingRef.current || !productId || newPrice === undefined) return
        submittingRef.current = true
        setSubmitting(true)
        try {
            await submitPriceCorrection(hoard.id, productId, newPrice, emitNow)
        } finally {
            submittingRef.current = false
            setSubmitting(false)
        }
    }

    const onCancel = () => closeModal(ModalId.CORRECT_HOARD_PRICE)

    return (
        <Form
            formId="correct-hoard-price-form"
            fields={fields}
            createLabel="Confirmar"
            disableSubmit={submitting || !plan || plan.newPrice === plan.previousPrice}
            onSubmit={onSubmit}
            onCancel={onCancel}
        />
    )
}

export const CorrectHoardPriceModal = (props: Props) => {
    const [key, setKey] = useState(0)
    const { openedModal } = useUIState()

    useEffect(() => {
        if (openedModal === ModalId.CORRECT_HOARD_PRICE) {
            setKey(prevKey => prevKey + 1)
        }
    }, [openedModal])

    return (
        <Modal
            modalId={ModalId.CORRECT_HOARD_PRICE}
            title="Corregir Precio"
            widthSize="m"
            actions={[]}
        >
            <CorrectHoardPriceForm key={key} {...props} />
        </Modal>
    )
}
